import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export type CouponDiscountType = 'percentage' | 'fixed';

export interface CouponDto {
  id: string;
  code: string;
  discountType: CouponDiscountType;
  discountValue: number;
  maxUses: number | null;
  usedCount: number;
  expiresAt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCouponInput {
  code: string;
  discountType: CouponDiscountType;
  discountValue: number;
  maxUses?: number;
  expiresAt?: string;
  isActive?: boolean;
}

export type UpdateCouponInput = Partial<Omit<CreateCouponInput, 'code'>>;

const QK = {
  list: ['coupons'] as const,
};

export function useCoupons() {
  return useQuery({
    queryKey: QK.list,
    queryFn: async () => {
      const res = await apiClient.get<CouponDto[]>('/shop/coupons');
      return res.data;
    },
  });
}

export function useCreateCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCouponInput) => {
      const res = await apiClient.post<CouponDto>('/shop/coupons', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.list }),
  });
}

export function useUpdateCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateCouponInput }) => {
      const res = await apiClient.patch<CouponDto>(`/shop/coupons/${id}`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.list }),
  });
}

export function useDeleteCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/shop/coupons/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.list }),
  });
}
